import React from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Box, Typography, Button } from '@mui/material';
import Iconify from '@/components/Iconify';
import { fCurrency } from '@/utils/formatNumber';
import useEventContext from '@/contexts/event/useEventContext';
import { usePromotionForm } from '@/sections/promotion';
import useTicketsContext from '@/contexts/tickets/useTicketsContext';
import useMobile from '@/hooks/useMobile';
import { useSaturated } from '@/store';

function TicketSelectedItem({ ticket }) {
  const { t } = useTranslation();
  const {
    event: {
      preferences: { currency, fee_breakdown },
    },
  } = useEventContext();
  const {
    name,
    category: { color },
    price,
  } = ticket;
  const { promotions } = usePromotionForm();
  const { isMobile } = useMobile();
  const { onDeleteTicket } = useTicketsContext();
  const { isEnabled } = useSaturated();

  const onDeleteTicketHandler = () => {
    onDeleteTicket(ticket);
  };

  const getZone = () => {
    if (ticket.seatsIoInfo) {
      const labels = ticket.seatsIoInfo;
      if (labels.parent) {
        return t('checkout.home.cart.seatSelected', {
          parent: labels.parent,
          own: labels.own,
        });
      } else {
        return t('checkout.home.cart.seatQuantity', {
          quantity: ticket.quantity,
          own: labels.own,
        });
      }
    }
    return t('checkout.home.cart.ticketsQuantity', {
      quantity: ticket.quantity,
    });
  };

  return (
    <Stack
      direction="row"
      spacing={0}
      sx={{
        boxShadow: (theme) => theme.customShadows.card,
        mt: 2,
        mb: 2,
        border: (theme) => `0.5px solid ${theme.palette.grey[900]}`,
        borderRadius: '16px',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0}>
        <Box
          sx={{
            width: '20px',
            height: '20px',
            color: color,
            ml: isMobile ? 1 : 2,
          }}
        >
          <Iconify width={20} height={20} icon={'material-symbols:circle'} />
        </Box>
        <Box sx={{ ml: isMobile ? 1 : 2, pb: 1.5, pt: 1.5 }}>
          <Stack direction="column">
            <Typography variant="subtitle1" component="div">
              {name}
            </Typography>
            <Typography
              variant="caption2"
              component="div"
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              {getZone()}
            </Typography>
          </Stack>
        </Box>
      </Stack>
      <Box sx={{ flexGrow: 1 }}></Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0}
        sx={{
          pr: isMobile ? 1 : 2,
        }}
      >
        {promotions && (
          <Typography
            variant="body2"
            component="div"
            sx={{ textAlign: 'right' }}
          >
            {`${fCurrency(price)} ${currency} `}
            <Typography
              variant="caption2"
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              {fee_breakdown === 'f' ? '' : t('checkout.home.cart.plusCharge')}
            </Typography>
          </Typography>
        )}
        {!promotions && (
          <Typography
            variant="body2"
            component="div"
            sx={{ minWidth: '155px', textAlign: 'right' }}
          >
            {`${fCurrency(price)} ${currency} `}
            <Typography
              variant="caption2"
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              {fee_breakdown === 'f' ? '' : t('checkout.home.cart.plusCharge')}
            </Typography>
          </Typography>
        )}
        {!isEnabled && (
          <Button
            sx={{
              'backgroundColor': (theme) => theme.palette.primary.palette[50],
              'display': 'flex',
              'alignItems': 'center',
              'pb': 1,
              'padding': 0,
              'ml': isMobile ? 1 : 2,
              'border': (theme) =>
                `0.5px solid ${theme.palette.primary.palette[100]}`,
              'borderRadius': '4px',
              'minWidth': '28px',
              '& svg': {
                color: (theme) => theme.palette.grey[900],
              },
            }}
            onClick={onDeleteTicketHandler}
          >
            <Iconify
              width={20}
              height={20}
              icon={'material-symbols:delete-outline'}
            />
          </Button>
        )}
      </Stack>
    </Stack>
  );
}

export default TicketSelectedItem;
